import { Controller } from "@hotwired/stimulus"

// Live replace warnings on the import review form when title/section change,
// plus SweetAlert summary before confirm when any included list already has entries.
export default class extends Controller {
  static targets = ["listCard", "warning"]
  static values = {
    existingCounts: Object
  }

  connect() {
    this.refreshWarnings()
  }

  refreshWarnings() {
    this.listCardTargets.forEach((listCard) => this.refreshListWarning(listCard))
  }

  refreshListWarning(listCard) {
    const warning = listCard.querySelector("[data-record-book-import-review-target='warning']")
    if (!warning) {
      return
    }

    const entryCount = this.existingCountForListCard(listCard)
    if (entryCount > 0) {
      const entryWord = entryCount === 1 ? "entry" : "entries"
      warning.textContent = `Already has ${entryCount} ${entryWord} — confirming replaces them (including any auto-generated holders).`
      warning.classList.remove("d-none")
    } else {
      warning.textContent = ""
      warning.classList.add("d-none")
    }
  }

  confirmReplace(event) {
    if (this.element.dataset.replaceConfirmed === "true") {
      return
    }

    const replacements = this.replacementSummaries()
    if (replacements.length === 0) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const summary = replacements.join("; ")
    const text = `Confirming replaces these lists and wipes any auto-generated holders: ${summary}.`

    if (typeof window.Swal === "undefined") {
      if (window.confirm(`Replace existing lists?\n\n${text}`)) {
        this.element.dataset.replaceConfirmed = "true"
        this.element.requestSubmit()
      }
      return
    }

    window.Swal.fire({
      title: "Replace existing lists?",
      text: text,
      showCancelButton: true,
      confirmButtonText: "Yes",
      keydownListenerCapture: true,
      confirmButtonAriaLabel: "Confirm replace",
      cancelButtonText: "Cancel",
      cancelButtonAriaLabel: "Cancel replace",
      customClass: {
        title: "color-fanword-black font-fanword-regular",
        confirmButton: "btn btn-sm px-4 py-2 text-decoration-none border-0 border-fanword-positive bg-fanword-positive color-fanword-black fanword-border-radius",
        cancelButton: "btn btn-sm px-4 py-2 text-decoration-none fanword-border bg-fanword-white color-fanword-black fanword-border-radius"
      }
    }).then((result) => {
      if (!result.isConfirmed) {
        return
      }

      this.element.dataset.replaceConfirmed = "true"
      this.element.requestSubmit()
    })
  }

  replacementSummaries() {
    const summaries = []

    this.listCardTargets.forEach((listCard) => {
      if (!this.listIncluded(listCard)) {
        return
      }

      const entryCount = this.existingCountForListCard(listCard)
      if (entryCount <= 0) {
        return
      }

      const title = this.listTitle(listCard).trim()
      const label = title || "Untitled list"
      const entryWord = entryCount === 1 ? "entry" : "entries"
      summaries.push(`"${label}" (${entryCount} ${entryWord})`)
    })

    return summaries
  }

  existingCountForListCard(listCard) {
    const recordScope = listCard.querySelector("[data-record-book-import-review-scope]")?.value || ""
    const section = this.cleanedSection(this.listSection(listCard))
    const title = this.listTitle(listCard).trim()
    const key = `${recordScope}|${section}|${title}`
    return Number(this.existingCountsValue[key] || 0)
  }

  listIncluded(listCard) {
    const includeCheckbox = listCard.querySelector("[data-record-book-import-review-include]")
    return includeCheckbox ? includeCheckbox.checked : true
  }

  listTitle(listCard) {
    return listCard.querySelector("[data-record-book-import-review-title]")?.value || ""
  }

  listSection(listCard) {
    return listCard.querySelector("[data-record-book-import-review-section]")?.value || ""
  }

  cleanedSection(section) {
    const text = (section || "").trim()
    if (!text) {
      return ""
    }

    if (["null", "undefined", "none", "n/a"].includes(text.toLowerCase())) {
      return ""
    }

    return text
  }
}
